import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { Plus, Clock, LayoutGrid, Rows3, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { CompanyLogo, Skeleton, UserAvatar } from "@/components/common";
import { ProjectTable } from "@/components/ProjectTable";
import { BarAction, Chips, PageBar, Segment } from "@/components/layout/PageBar";
import { STATUS_LABEL, money, ago } from "@/lib/sk";

const WORK = ["draft", "configured", "quoted"];
const STAGES = [
  { key: "draft", hint: "Krav struktureres", color: "hsl(210 100% 14% / 0.28)" },
  { key: "configured", hint: "Regelmotoren har dimensjonert", color: "hsl(160 45% 38%)" },
  { key: "quoted", hint: "Tilbud skrevet, ikke sendt", color: "hsl(35 80% 48%)" },
  { key: "sent", hint: "Hos kunden", color: "hsl(208 95% 52%)" },
  { key: "negotiation", hint: "Avklaringer og forhandling", color: "hsl(268 35% 48%)" },
];
const CLOSED = [
  { key: "won", hint: "Kontrakt", color: "hsl(152 50% 36%)" },
  { key: "lost", hint: "Lærdom", color: "hsl(0 50% 48%)" },
];
/** Stages in Egil's words – never an internal state name like «configured». */
const STAGE_LABEL = { draft: "Under arbeid", configured: "Kalkuleres", calculated: "Kalkuleres", quoted: "Klar til sending", sent: "Sendt", negotiation: "Forhandling", won: "Vunnet", lost: "Tapt" };
const stageLabel = (k) => STAGE_LABEL[k] || stageLabel(k) || k;
const kr = (v) => `${money(v, "NOK", 1, { bare: true })} kr`;
const colFor = (status) => (status === "calculated" ? "configured" : status);
const VIEW_KEY = "sk.pipeline.view";
const FEW = 8;

function Card({ p, index, onOpen }) {
  return (
    <Draggable draggableId={p.id} index={index}>
      {(prov, snap) => (
        <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps} onClick={() => onOpen(p)} className={cn("group cursor-pointer rounded-[16px] border border-black/[0.06] bg-white p-3.5 shadow-[0_1px_2px_hsl(212_40%_16%/0.03)] transition-[box-shadow,border-color,transform] duration-150 hover:-translate-y-px hover:border-black/[0.14] hover:shadow-md-soft", snap.isDragging && "rotate-[0.6deg] shadow-float")} data-testid={`pipeline-card-${p.number}`}>
          <div className="flex items-start gap-3">
            <CompanyLogo name={p.customer?.name} domain={p.customer?.website} size="sm" className="h-9 w-9 rounded-[10px] p-1" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13.5px] font-medium leading-snug">{p.name}</div>
              <div className="mt-0.5 truncate text-[12px] text-muted-foreground">{p.customer?.name || "Ukjent kunde"}{p.vessel?.name ? ` · ${p.vessel.name}` : ""}</div>
            </div>
          </div>
          <div className="mt-3.5 flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2.5 text-[12px] text-muted-foreground">
              {p.items_count ? <span className="inline-flex shrink-0 items-center gap-1"><FileSpreadsheet className="h-3 w-3" />{p.items_count}</span> : null}
              {p.clarification_pending ? <span className="inline-flex shrink-0 items-center gap-1 text-[hsl(var(--v-assumed))]"><Clock className="h-3 w-3" /> kunde</span> : null}
              <span className="truncate text-muted-foreground/60">{ago(p.updated_at)}</span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {p.value ? <span className="whitespace-nowrap font-mono text-[12.5px] tabular-nums text-foreground/85">{kr(p.value)}</span> : null}
              <UserAvatar name={p.owner?.name} initials={p.owner?.initials} color={p.owner?.color} src={p.owner?.avatar_url} size="sm" className="h-5 w-5" />
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
}

function Column({ col, items, onOpen }) {
  const sum = items.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <Droppable droppableId={col.key}>
      {(prov, snap) => (
        <div ref={prov.innerRef} {...prov.droppableProps} className={cn("flex w-[280px] shrink-0 flex-col rounded-[20px] p-1.5 transition-colors", snap.isDraggingOver && "bg-[hsl(var(--brand-soft))]")} data-testid={`pipeline-column-${col.key}`}>
          <div className="px-2.5 pb-3 pt-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[13px] font-semibold"><span className="h-2 w-2 rounded-full" style={{ background: col.color }} />{stageLabel(col.key)}<span className="font-mono text-[11px] font-medium text-muted-foreground">{items.length}</span></div>
              <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">{sum ? kr(sum) : ""}</span>
            </div>
          </div>
          <div className="flex min-h-[80px] flex-1 flex-col gap-2">
            {items.map((p, i) => <Card key={p.id} p={p} index={i} onOpen={onOpen} />)}
            {prov.placeholder}
            {!items.length && !snap.isDraggingOver ? <div className="rounded-[14px] border border-dashed border-black/[0.08] py-7 text-center text-[11.5px] text-muted-foreground/50">Ingen her nå</div> : null}
          </div>
        </div>
      )}
    </Droppable>
  );
}

/** Pipeline = the same projects, grouped by status. Board for flow, list for overview. */
export default function PipelinePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [params, setParams] = useSearchParams();
  const owner = params.get("owner") || "all";
  const showClosed = params.get("closed") === "1";
  const focus = params.get("stage") || null;
  const view = params.get("view") || localStorage.getItem(VIEW_KEY) || (rows && rows.length < FEW ? "list" : "board");
  const setParam = (k, v) => setParams((prev) => { const n = new URLSearchParams(prev); if (v == null || v === "" || v === "all" || v === false) n.delete(k); else n.set(k, String(v)); return n; }, { replace: true });
  const setOwner = (v) => setParam("owner", v);
  const setShowClosed = (v) => setParam("closed", (typeof v === "function" ? v(showClosed) : v) ? "1" : null);
  const setFocus = (v) => setParam("stage", v);

  const load = () => api.get("/projects", { params: { limit: 200 } }).then((r) => setRows((r.data || []).filter((p) => p.status !== "new"))).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const changeView = (v) => { setParam("view", v); localStorage.setItem(VIEW_KEY, v); };

  const owners = useMemo(() => {
    const m = new Map();
    (rows || []).forEach((p) => p.owner?.name && !m.has(p.owner_id) && m.set(p.owner_id, p.owner));
    return Array.from(m.entries());
  }, [rows]);
  const list = useMemo(() => (rows || []).filter((p) => owner === "all" || (owner === "mine" ? p.owner_id === user?.id : p.owner_id === owner)), [rows, owner, user]);
  const open = list.filter((p) => !["won", "lost"].includes(p.status));
  const out = open.filter((p) => ["sent", "negotiation"].includes(p.status));
  const stages = showClosed ? [...STAGES, ...CLOSED] : STAGES;
  const visible = focus ? list.filter((p) => colFor(p.status) === focus) : showClosed ? list : open;
  const cols = focus ? stages.filter((c) => c.key === focus) : stages;

  const onDragEnd = async ({ destination, source, draggableId }) => {
    if (!destination || destination.droppableId === source.droppableId) return;
    const to = destination.droppableId;
    const p = rows.find((x) => x.id === draggableId);
    if (!p) return;
    const fromWork = WORK.includes(colFor(p.status));
    if (WORK.includes(to) && !fromWork) return toast("Tilbake til arbeid? Gjør det fra arbeidsområdet – da bevares tilbudshistorikken.");
    if (to === "sent") return toast("Tilbud sendes fra arbeidsområdet, så kunden får lenke og versjon registrert.");
    if (["negotiation", "won", "lost"].includes(to) && fromWork) return toast("Send tilbudet fra arbeidsområdet før det går til forhandling.");
    const prev = rows;
    setRows(rows.map((x) => (x.id === p.id ? { ...x, status: to } : x)));
    try {
      await api.patch(`/projects/${p.id}`, { status: to });
      toast.success(`#${p.number} flyttet til ${stageLabel(to)}`);
      window.dispatchEvent(new Event("projects:changed"));
    } catch (e) {
      setRows(prev);
      toast.error(errMsg(e));
    }
  };

  const openSum = open.reduce((s2, p) => s2 + (p.value || 0), 0);
  const meta = rows ? [`${open.length} ${open.length === 1 ? "aktivt tilbud" : "aktive tilbud"}`, openSum ? kr(openSum) : null, out.length ? `${out.length} sendt` : null].filter(Boolean).join(" · ") : null;
  const ownerItems = [["all", "Alle"], ["mine", "Mine"], ...owners.filter(([id]) => id !== user?.id).map(([id, o]) => [id, (o.name || "").split(" ")[0]])];

  return (
    <div className="flex h-full flex-col" data-testid="pipeline-page">
      <div className="mx-auto w-full max-w-[1440px] px-5 pt-5 sm:px-8">
        <PageBar title="Tilbudsoversikt" meta={meta} testId="pipeline-bar" action={<BarAction onClick={() => navigate("/ny")} testId="pipeline-new-button"><Plus className="h-4 w-4" strokeWidth={2.2} /> Nytt arbeid</BarAction>}>
          {ownerItems.length > 2 ? <Chips value={owner} onChange={setOwner} items={ownerItems} label="Ansvarlig" testId="pipeline-owner" /> : null}
          <button type="button" onClick={() => { setShowClosed((v) => !v); setFocus(null); }} aria-pressed={showClosed} className={cn("h-8 rounded-[8px] px-2.5 text-[13.5px] transition-colors", showClosed ? "bg-black/[0.06] font-medium text-foreground" : "text-foreground/55 hover:bg-black/[0.035] hover:text-foreground/85")} data-testid="pipeline-toggle-closed-button">Vunnet / tapt</button>
          <Segment value={view} onChange={changeView} items={[["board", "Tavle", LayoutGrid], ["list", "Liste", Rows3]]} label="Visning" testId="pipeline-view" />
        </PageBar>
      </div>

      {view === "board" ? (
        <div className="min-h-0 flex-1 overflow-x-auto pb-8 pt-5 scrollbar-thin">
          <div className="mx-auto w-full max-w-[1440px] px-5 sm:px-8">
            {!rows ? (
              <div className="flex gap-3">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-72 w-[280px] shrink-0 rounded-[20px]" />)}</div>
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <div className="flex h-full items-start gap-2" data-testid="pipeline-board">
                  {cols.map((c) => <Column key={c.key} col={c} items={list.filter((p) => colFor(p.status) === c.key)} onOpen={(p) => navigate(`/p/${p.id}`)} />)}
                </div>
              </DragDropContext>
            )}
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto pb-10 pt-5 scrollbar-thin">
          <div className="mx-auto w-full max-w-[1440px] px-5 sm:px-8">
            {!rows ? <Skeleton className="h-64 rounded-[18px]" /> : <ProjectTable rows={visible} groupByStatus testPrefix="pipeline" emptyText="Ingen prosjekter i dette utvalget." />}
          </div>
        </div>
      )}
    </div>
  );
}
